import { scheduling } from "lobbie";
import { useCallback, useEffect, useMemo, useState } from "react";
import PatientSchedulingCard from "src/components/patients/scheduling/cards/PatientSchedulingCard";
import { TPatientSchedulingItem } from "src/components/patients/scheduling/types";
import SelectedCircle from "src/components/shared/elements/icons/SelectedCircle";
import UnSelectedCircle from "src/components/shared/elements/icons/UnselectedCircle";
import { DATE_TIME_FORMATS } from "src/constants";
import { dayjsFormatUnix } from "src/utils";
import Avatar from "../../Avatar";

interface IProps {
    practitioner: scheduling.IPractitionerNextAvailable;
    selected: boolean;
    hideSelectCircle?: boolean;
    setSelected: (practitioner: scheduling.IPractitionerNextAvailable | null) => void;
    disabled?: boolean;
    timeZone?: string;
}

const AVATAR_STYLE = { width: 45, height: 45 };

const SchedulingPractitionerCard: React.FC<IProps> = ({
    practitioner,
    selected,
    hideSelectCircle,
    setSelected,
    disabled,
    timeZone,
}) => {
    const handleError = useCallback(() => setSource(null), []);
    const [source, setSource] = useState<string | null>(practitioner?.signedURL || "");

    useEffect(() => {
        if (!source && source !== null && practitioner?.signedURL) {
            setSource(practitioner.signedURL);
        }
    }, [source, practitioner?.signedURL]);

    const avatar = useMemo(() => {
        if (source) {
            return (
                <Avatar
                    onError={handleError}
                    src={source}
                    alt={practitioner.displayName}
                    style={AVATAR_STYLE}
                />
            );
        } else {
            return (
                <Avatar
                    src="/doctor_avatar.png"
                    alt={practitioner.displayName}
                    style={AVATAR_STYLE}
                />
            );
        }
    }, [source, handleError, practitioner.displayName]);

    const subtitle = useMemo(() => {
        if (practitioner.nextAvailableStartUnix && timeZone) {
            return `Next available: ${dayjsFormatUnix(
                practitioner.nextAvailableStartUnix,
                timeZone,
                DATE_TIME_FORMATS.Scheduler.BlockDateTime12HFormatCondensedAMPM,
            )}`;
        } else {
            return "Next available: No upcoming availability";
        }
    }, [practitioner, timeZone]);

    return (
        <PatientSchedulingCard
            item={practitioner}
            selected={selected}
            setSelected={setSelected as (item: TPatientSchedulingItem | null) => void}
            disabled={disabled}
        >
            <div className="row">
                <div className="col-8 text-left pr-0">
                    <span className={`body-medium bold ${disabled ? "gray3" : "blue"}`}>
                        {practitioner.displayName}
                    </span>
                </div>
                <div className={`col-${hideSelectCircle ? 4 : 3} text-right px-0`}>{avatar}</div>
                {!hideSelectCircle && (
                    <div className="col-1 text-right pl-0">
                        <div>
                            {selected ? (
                                <SelectedCircle disabled={disabled} />
                            ) : (
                                <UnSelectedCircle />
                            )}
                        </div>
                    </div>
                )}
            </div>
            <span className={disabled ? "gray3" : ""}>{subtitle}</span>
        </PatientSchedulingCard>
    );
};

export default SchedulingPractitionerCard;
